import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "react-router-dom";

import { ArticlesLibraryCreateButton } from "@/components/ui/admin/articles/ArticlesLibraryCreateButton";
import { ArticlesLibraryKpis as ArticlesLibraryKpisPanel } from "@/components/ui/admin/articles/ArticlesLibraryKpis";
import { ArticlesLibraryPanel } from "@/components/ui/admin/articles/ArticlesLibraryPanel";
import { createArticleService } from "@/core/services/factory";
import {
  parseArticleLibraryTab,
  parseArticleLibrarySort,
  parseArticlePage,
  parseArticlePageSize,
  parseArticleSearchQuery,
  type ArticleAdminListItem,
} from "@/core/services/types/article.types";
import { useLibraryResultsFreeze } from "@/hooks/useLibraryResultsFreeze";
import { useLibrarySearchSnapshot } from "@/hooks/useLibrarySearchSnapshot";
import { useTranslations } from "@/lib/useTranslations";

export function ArticlesLibraryPage() {
  useLibrarySearchSnapshot("articles");
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tab = parseArticleLibraryTab(searchParams.get("status") ?? undefined);
  const page = parseArticlePage(searchParams.get("page") ?? undefined);
  const pageSize = parseArticlePageSize(searchParams.get("size") ?? undefined);
  const searchQuery = parseArticleSearchQuery(searchParams.get("q") ?? undefined);
  const sort = parseArticleLibrarySort(
    searchParams.get("sort") ?? undefined,
    searchParams.get("dir") ?? undefined,
  );
  const t = useTranslations("articles.library");
  const queryFingerprint = location.search;

  const { data: stats } = useQuery({
    queryKey: ["articles-library-stats"],
    queryFn: async () => {
      const articles = createArticleService();
      return articles.getLibraryStats();
    },
    staleTime: 60_000,
  });

  const { data: listResult, isFetching: isListFetching } = useQuery({
    queryKey: ["articles-library-list", tab, page, pageSize, searchQuery, sort?.column, sort?.direction],
    queryFn: async () => {
      const articles = createArticleService();
      return articles.listByLibraryTab(tab, page, pageSize, searchQuery, sort);
    },
    placeholderData: keepPreviousData,
  });

  const {
    isFrozen,
    displayResult,
    toggleFreeze,
    canFreeze,
  } = useLibraryResultsFreeze<ArticleAdminListItem>(
    "articles",
    queryFingerprint,
    listResult,
  );

  if (!stats || !displayResult) {
    return (
      <div className="mx-auto flex w-full flex-1 items-center justify-center px-4 py-10 text-sm text-neutral-500 lg:px-8">
        Chargement…
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full flex-1 flex-col gap-6 overflow-y-auto px-4 py-10 lg:px-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium uppercase tracking-widest text-neutral-500">
            {t("eyebrow")}
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-whart-text">
            {t("title")}
          </h1>
          <p className="text-sm text-neutral-600">{t("description")}</p>
        </div>
        <ArticlesLibraryCreateButton />
      </header>

      <ArticlesLibraryKpisPanel
        kpis={stats.kpis}
        sectionLabel={t("kpisNav")}
        labels={{
          total: t("kpis.total"),
          publishedThisMonth: t("kpis.publishedThisMonth"),
          pendingReview: t("kpis.pendingReview"),
        }}
      />

      <ArticlesLibraryPanel
        items={displayResult.items}
        total={displayResult.total}
        page={displayResult.page}
        pageSize={displayResult.pageSize}
        tab={tab}
        tabCounts={stats.tabCounts}
        searchQuery={searchQuery}
        sort={sort}
        isFrozen={isFrozen}
        onToggleResultsFreeze={toggleFreeze}
        canFreezeResults={canFreeze}
        isRefreshing={!isFrozen && isListFetching}
      />
    </div>
  );
}
